import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Linkedin, ArrowRight } from "lucide-react";
import { useSEO } from "../lib/seo";
import { fadeInUp, CONTACT } from "../lib/brand";
import { SERVICES } from "../data/services";
import { Eyebrow, GoldRule } from "../components/site/Primitives";
import { RidgeLines } from "../components/site/Marks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", role: "", interest: "", message: "" };

const STEPS = [
  { title: "We respond promptly", copy: "You'll hear from Summit directly to arrange a time." },
  { title: "A focused conversation", copy: "Your context, your goals, and the organizational realities in play." },
  { title: "A clear recommendation", copy: "A candid perspective on next steps—whether or not they involve Summit." },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.16em] text-summit-text-muted mb-1 font-body">{label}</span>
    {children}
  </label>
);

const inputCls = "form-input w-full font-body text-sm text-summit-charcoal placeholder:text-summit-text-muted/60";

export default function Contact() {
  useSEO(
    "Schedule a Strategy Conversation",
    "Contact Summit Transformation Group to schedule a strategy conversation about AI adoption, organizational transformation, strategic planning, or leadership development."
  );
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. We'll be in touch shortly to schedule your conversation.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section data-testid="contact-hero" className="relative pt-40 pb-24 md:pt-48 md:pb-32 bg-summit-bg overflow-hidden">
        <RidgeLines className="absolute inset-x-0 bottom-0 h-72 w-full opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          <motion.div {...fadeInUp} className="lg:col-span-5">
            <Eyebrow className="mb-6">Contact</Eyebrow>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-summit-charcoal">
              Schedule a Strategy Conversation
            </h1>
            <GoldRule className="my-8" />
            <p className="font-body text-base md:text-lg text-summit-text-muted leading-relaxed">
              A focused, no-obligation discussion about where your organization is, where it is going, and whether Summit is the right partner.
            </p>

            <ol className="mt-10 space-y-6">
              {STEPS.map((s, i) => (
                <li key={s.title} className="flex gap-4" data-testid={`contact-step-${i}`}>
                  <span className="font-heading text-2xl text-summit-gold-dark leading-none w-8 shrink-0">0{i + 1}</span>
                  <span>
                    <span className="block font-heading text-xl text-summit-charcoal">{s.title}</span>
                    <span className="block mt-1 font-body text-sm text-summit-text-muted leading-relaxed">{s.copy}</span>
                  </span>
                </li>
              ))}
            </ol>

            <div className="mt-12 pt-8 border-t border-summit-border space-y-3 font-body text-sm text-summit-charcoal">
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-3 hover:text-summit-gold-dark transition-colors"><Mail size={16} className="text-summit-gold-dark" />{CONTACT.email}</a>
              <p className="flex items-center gap-3"><Phone size={16} className="text-summit-gold-dark" />{CONTACT.phone}</p>
              <p className="flex items-center gap-3"><MapPin size={16} className="text-summit-gold-dark" />{CONTACT.location}</p>
              <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin-link" className="flex items-center gap-3 hover:text-summit-gold-dark transition-colors"><Linkedin size={16} className="text-summit-gold-dark" />Connect on LinkedIn</a>
            </div>
          </motion.div>

          <motion.div {...fadeInUp} transition={{ ...fadeInUp.transition, delay: 0.15 }} className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="bg-white border border-summit-border p-8 md:p-12 space-y-7 shadow-[0_30px_80px_-40px_rgba(44,62,53,0.25)]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <Field label="Name *"><input data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" /></Field>
                <Field label="Email *"><input data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@organization.org" /></Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <Field label="Organization"><input data-testid="contact-company-input" value={form.company} onChange={set("company")} className={inputCls} placeholder="Institution or company" /></Field>
                <Field label="Role / Title"><input data-testid="contact-role-input" value={form.role} onChange={set("role")} className={inputCls} placeholder="e.g., Provost, CIO, COO" /></Field>
              </div>
              <Field label="Primary area of interest">
                <select data-testid="contact-interest-select" value={form.interest} onChange={set("interest")} className="form-input">
                  <option value="">Select an area</option>
                  {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                  <option value="Not sure yet">Not sure yet</option>
                </select>
              </Field>
              <Field label="What are you working toward? *">
                <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Briefly describe your organization's situation, the change or AI initiative you're considering, and what a successful outcome would look like." />
              </Field>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="group w-full inline-flex items-center justify-center gap-3 bg-summit-forest hover:bg-summit-forest-dark text-white font-body text-sm font-medium tracking-wide py-4 transition-colors disabled:opacity-60"
              >
                {loading ? "Sending…" : "Request a Strategy Conversation"}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <p className="font-body text-xs text-summit-text-muted text-center">Your information is used only to respond to your inquiry.</p>
            </form>
          </motion.div>
        </div>
      </section>
    </>
  );
}
